import { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'

import { getDocuments, getFile, getQuoteByReferenceNumber } from '../../../api/api'
import { FileUpload } from '@components/file-upload'
import { AddFileButton } from '@components/buttons'
import { FILE_UPLOAD, PARAM_NAMES } from '../../../constants'
import { MIME_TYPES_MAP } from '../../../variable-maps'
import { AdasContainer, AdasButton, AdasBox, AdasTypography } from '@components/wrapper-components'
import { useScrollTop } from '../../../utils/useScrollTop'
import { Logger } from '../../../logger'

// Build a Blob URL out of a fetched file response so the FileUpload tile can use
// it directly (and so we can show one page-level loader instead of one spinner
// per tile + one for the page chrome).
const buildBlobUrl = (data, name = '') => {
  const ext = name?.includes('.') ? name.slice(name.lastIndexOf('.')).toLowerCase() : ''
  const mime = MIME_TYPES_MAP[ext] || 'application/octet-stream'
  const blob = new Blob([data], { type: mime })
  return { url: URL.createObjectURL(blob), size: blob.size }
}

export const QuoteDocument = () => {
  useScrollTop()
  const { [PARAM_NAMES.REFERENCE_NUMBER]: referenceNumber } = useParams()
  const history = useHistory()
  const [quoteData, setQuoteData] = useState({})
  const [documents, setDocuments] = useState({
    collisionRepairEstimate: '',
    calibrationRequiredReport: '',
    partsInvoice: ''
  })
  const [additionalDocuments, setAdditionalDocuments] = useState([])
  const [loadedAdditionalDocs, setLoadedAdditionalDocs] = useState([])
  const [pageLoading, setPageLoading] = useState(true)

  const removeFileHandler = (key) => {
    setDocuments((prev) => ({ ...prev, [key]: null }))
  }

  const removeLoadedAdditionalFileHandler = (index) => {
    setLoadedAdditionalDocs((prev) => {
      const data = [...prev]
      if (data[index]) data[index].removed = true
      return data
    })
  }

  // Single bootstrap effect: fetch the quote, then the documents list, then
  // prefetch every attachment blob in parallel. Only flip pageLoading off after
  // all of them resolve so the user sees one loader instead of cascading ones.
  useEffect(() => {
    if (!referenceNumber) return undefined
    let cancelled = false
    const blobUrls = []

    const bootstrap = async () => {
      setPageLoading(true)
      try {
        const quoteRes = await getQuoteByReferenceNumber({ referenceNumber })
        if (cancelled) return
        const quote = quoteRes?.data?.quote || {}
        setQuoteData(quote)
        if (!quote.id) return

        const docsRes = await getDocuments({ type: 'quotes', id: quote.id })
        if (cancelled) return
        const docData = docsRes?.data || {}

        // Collect every attachment metadata that needs a blob fetch.
        const named = {
          collisionRepairEstimate: docData.collisionRepairEstimate || '',
          calibrationRequiredReport: docData.calibrationRequiredReport || '',
          partsInvoice: docData.partsInvoice || ''
        }
        const additional = Array.isArray(docData.additionalDocuments) ? docData.additionalDocuments : []

        const namedEntries = Object.entries(named).filter(([, v]) => v && v.id)
        const additionalEntries = additional.filter((d) => d && d.id)

        // Fire all blob fetches in parallel — one shared loader.
        const namedBlobResults = await Promise.all(
          namedEntries.map(async ([key, meta]) => {
            try {
              const r = await getFile({ attachmentId: meta.id })
              const { url, size } = buildBlobUrl(r.data, meta.name)
              blobUrls.push(url)
              return [key, { ...meta, preloadedUrl: url, preloadedSize: size }]
            } catch (err) {
              Logger.error({ message: err, payload: { file: 'QuoteDocument/prefetch named', id: meta.id } })
              return [key, meta]
            }
          })
        )

        const additionalBlobResults = await Promise.all(
          additionalEntries.map(async (meta) => {
            try {
              const r = await getFile({ attachmentId: meta.id })
              const { url, size } = buildBlobUrl(r.data, meta.name)
              blobUrls.push(url)
              return { ...meta, preloadedUrl: url, preloadedSize: size }
            } catch (err) {
              Logger.error({ message: err, payload: { file: 'QuoteDocument/prefetch additional', id: meta.id } })
              return meta
            }
          })
        )

        if (cancelled) return
        const mergedNamed = { ...named }
        namedBlobResults.forEach(([key, val]) => {
          mergedNamed[key] = val
        })
        setDocuments((prev) => ({ ...prev, ...mergedNamed }))
        setLoadedAdditionalDocs(additionalBlobResults)
      } catch (err) {
        Logger.error({ message: err, payload: { referenceNumber, file: 'QuoteDocument/bootstrap' } })
      } finally {
        if (!cancelled) setPageLoading(false)
      }
    }

    bootstrap()

    return () => {
      cancelled = true
      // Best-effort cleanup of object URLs we created.
      blobUrls.forEach((u) => URL.revokeObjectURL(u))
    }
  }, [referenceNumber])

  const vehicle = quoteData.vehicle || {}
  const vehicleTitle = [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(' ')

  // Track how many of the visible slots have an attached file. Three named slots
  // + N already-loaded additionals + N user-added (in-flight) additionals; the
  // trailing "Add Additional Document" button isn't counted as a slot.
  const { attached, total } = useMemo(() => {
    const named = ['collisionRepairEstimate', 'calibrationRequiredReport', 'partsInvoice']
    const namedAttached = named.reduce((n, k) => n + (documents[k] ? 1 : 0), 0)
    const loadedActive = loadedAdditionalDocs.filter((d) => d && !d.removed).length
    const additionalActive = additionalDocuments.filter((d) => d && !d.removed).length
    const slotCount = 3 + loadedAdditionalDocs.length + additionalDocuments.length + 1 // +1 for "Add Additional Document"
    return { attached: namedAttached + loadedActive + additionalActive, total: slotCount }
  }, [documents, loadedAdditionalDocs, additionalDocuments])

  if (pageLoading) {
    return (
      <AdasContainer maxWidth={false}>
        <AdasBox className='flex min-h-[60vh] flex-col items-center justify-center gap-3'>
          <Loader2 className='h-7 w-7 animate-spin text-[#1F6FEB]' />
          <AdasTypography className='text-[14px] text-[#475569]'>Loading documents...</AdasTypography>
        </AdasBox>
      </AdasContainer>
    )
  }

  return (
    <AdasContainer maxWidth={false}>
      <AdasBox className='mb-[60px] mt-5'>
        {/* Header — Back link + title (desktop only); mobile shows the meta line */}
        <AdasBox className='mb-4'>
          <AdasBox className='hidden min-[435px]:block'>
            <button
              type='button'
              onClick={() => history.goBack()}
              className='mb-1 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#1F6FEB] hover:underline'
            >
              Back to Quote Summary
            </button>
            <AdasTypography className='text-[28px] font-bold leading-tight text-[#0A1628]'>Documents</AdasTypography>
          </AdasBox>
          {quoteData.id && (
            <AdasBox className='min-[435px]:mt-1 text-[13px] text-[#475569]'>
              <AdasBox className='flex flex-wrap items-center gap-x-2'>
                <span>
                  <span>Quote# </span>
                  <span className='font-mono'>{quoteData.reference_number}</span>
                </span>
                {quoteData.repair_order_num && (
                  <>
                    <span className='hidden min-[435px]:inline text-[#CBD5E1]'>·</span>
                    <span>
                      <span>RO# </span>
                      <span className='font-mono'>{quoteData.repair_order_num}</span>
                    </span>
                  </>
                )}
                {vehicleTitle && (
                  <>
                    {/* Desktop: middle dot + vehicle inline. Mobile: vehicle on its own line. */}
                    <span className='hidden min-[435px]:inline text-[#CBD5E1]'>·</span>
                    <span className='hidden min-[435px]:inline'>{vehicleTitle}</span>
                  </>
                )}
              </AdasBox>
              {vehicleTitle && (
                <AdasTypography className='mt-0.5 min-[435px]:hidden text-[13px] text-[#475569]'>{vehicleTitle}</AdasTypography>
              )}
            </AdasBox>
          )}
        </AdasBox>

        {/* Documents card */}
        <AdasBox className='mb-4 rounded-xl border border-[#EBEDF0] bg-white px-6 py-5 shadow-sm'>
          <AdasBox className='mb-4 flex items-center justify-between'>
            <AdasTypography className='text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>Documents</AdasTypography>
            <AdasTypography className='text-[13px] text-[#475569]'>
              <span className='font-bold text-[#0A1628]'>{attached}</span>
              <span className='ml-1'>of {total} attached</span>
            </AdasTypography>
          </AdasBox>

          <AdasBox className='flex flex-col gap-3'>
            <FileUpload
              variant='row'
              removeFile={() => removeFileHandler('collisionRepairEstimate')}
              existingData={documents.collisionRepairEstimate}
              workOrderData={quoteData}
              title={FILE_UPLOAD.TITLE.UPLOAD_COLLISION_REPAIR_ESTIMATE}
              label={FILE_UPLOAD.LABEL.COLLISION_REPAIR_ESTIMATE}
              description='PDF from CCC, Mitchell, Audatex — used to reconstruct line items'
              typeLabel={FILE_UPLOAD.TYPE_LABEL.PRE_CALIBRATION_REPAIR_ESTIMATE_SCAN}
            />
            <FileUpload
              variant='row'
              removeFile={() => removeFileHandler('calibrationRequiredReport')}
              existingData={documents.calibrationRequiredReport}
              workOrderData={quoteData}
              title={FILE_UPLOAD.TITLE.UPLOAD_CALIBRATION_REQUIRED_REPORT}
              label={FILE_UPLOAD.LABEL.CALIBRATION_REQUIRED_REPORT}
              description='OEM scan tool report — confirms which calibrations the vehicle needs'
              typeLabel={FILE_UPLOAD.TYPE_LABEL.CALIBRATION_REQUIRED_REPORT}
            />
            <FileUpload
              variant='row'
              removeFile={() => removeFileHandler('partsInvoice')}
              existingData={documents.partsInvoice}
              workOrderData={quoteData}
              title={FILE_UPLOAD.TITLE.UPLOAD_PARTS_INVOICE}
              label={FILE_UPLOAD.LABEL.PARTS_INVOICE}
              description='Invoice from your parts supplier'
              typeLabel={FILE_UPLOAD.TYPE_LABEL.PARTS_INVOICE}
            />
            {loadedAdditionalDocs.map((doc, index) =>
              doc && !doc.removed ? (
                <FileUpload
                  key={`loaded-${index}`}
                  variant='row'
                  removeFile={() => removeLoadedAdditionalFileHandler(index)}
                  existingData={doc}
                  workOrderData={quoteData}
                  title={`custom_document_${index}`}
                  label={FILE_UPLOAD.LABEL.ADDITIONAL_DOCUMENT}
                  typeLabel={FILE_UPLOAD.TYPE_LABEL.ADDITIONAL_DOCUMENT}
                />
              ) : null
            )}
            {additionalDocuments.map((doc, index) =>
              doc && !doc.removed ? (
                <FileUpload
                  key={`additional-${index}`}
                  variant='row'
                  additionalDocument={doc}
                  workOrderData={quoteData}
                  title={`custom_document_${index}`}
                  label={FILE_UPLOAD.LABEL.ADDITIONAL_DOCUMENT}
                  typeLabel={FILE_UPLOAD.TYPE_LABEL.ADDITIONAL_DOCUMENT}
                />
              ) : null
            )}
            <AddFileButton setAdditionalDocuments={setAdditionalDocuments} />
          </AdasBox>
        </AdasBox>

        {/* Footer — DONE (filled navy) + Cancel (outlined) */}
        <AdasBox className='flex items-center gap-3'>
          <AdasButton
            variant='contained'
            onClick={() => history.goBack()}
            className='rounded-lg bg-[#0A1628] px-6 font-semibold text-white hover:bg-[#1F2D3D]'
          >
            DONE
          </AdasButton>
          <AdasButton variant='outlined' onClick={() => history.goBack()} className='rounded-lg px-6 font-semibold'>
            Cancel
          </AdasButton>
        </AdasBox>
      </AdasBox>
    </AdasContainer>
  )
}
